import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, FileBarChart2, Crown, AlertOctagon, ShieldCheck, Clock, CheckCircle2, XCircle, ExternalLink, Activity, Globe } from "lucide-react";
import GlobalTrafficDashboard from "@/components/GlobalTrafficDashboard";
import RoyaiCodesAdmin from "@/components/RoyaiCodesAdmin";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [evidenceFilter, setEvidenceFilter] = useState("pending");
  const [tab, setTab] = useState("users");
  const [error, setError] = useState("");

  const loadEvidence = async (status = evidenceFilter) => {
    try {
      const { data } = await api.get(`/admin/evidence${status ? `?status_filter=${status}` : ""}`);
      setEvidence(data.submissions || []);
    } catch (err) { setError(formatApiError(err)); }
  };

  const reviewEvidence = async (id, status, notes = "") => {
    try {
      const res = await api.post(`/admin/evidence/${id}/review`, { status, notes });
      const d = res.data || {};
      if (status === "approved") {
        const resolved = (d.resolved_bounties_direct || 0) + (d.resolved_bounties_by_audit || 0);
        const bits = [];
        if (d.score_delta) bits.push(`Score +${d.score_delta}`);
        if (resolved) bits.push(`${resolved} bounty resolved`);
        if (d.new_bounties) bits.push(`${d.new_bounties} new bounty`);
        toast.success(`Approved. ${bits.length ? bits.join(" · ") + "." : "RoyAI recalculating…"}`);
      } else {
        toast.success("Evidence rejected.");
      }
      await loadEvidence();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = async () => {
    try {
      const [s, u, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/assessments"),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setAssessments(a.data.assessments || []);
    } catch (err) { setError(formatApiError(err)); }
  };

  useEffect(() => { load(); loadEvidence("pending"); }, []);
  useEffect(() => { if (tab === "evidence") loadEvidence(evidenceFilter); }, [tab, evidenceFilter]);

  const toggleMembership = async (userId, status) => {
    const newStatus = status === "active" ? "free" : "active";
    try {
      await api.post("/admin/membership", { user_id: userId, membership_status: newStatus });
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const toggleIdentityVerified = async (userId, current) => {
    try {
      await api.post("/admin/identity-verify", { user_id: userId, verified: !current });
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="p-12 text-rose-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="px-6 md:px-10 lg:px-16 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-overline">Admin Console</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mt-2">
              BeatCredsi<span className="text-amber-500">™</span> <span className="text-amber-500">control room.</span>
            </h1>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-px bg-white/5 border border-white/5 mb-12" data-testid="admin-stats">
            {[
              { icon: Users, label: "Registered Members", val: (stats.registered_members ?? stats.total_users ?? 0).toLocaleString(), testid: "stat-registered-members" },
              { icon: Globe, label: "Total Visitors", val: (stats.total_visitors ?? 0).toLocaleString(), testid: "stat-total-visitors" },
              { icon: Activity, label: "Total Requests", val: (stats.total_api_requests ?? 0).toLocaleString(), testid: "stat-total-requests" },
              { icon: FileBarChart2, label: "Assessments", val: (stats.total_assessments ?? 0).toLocaleString(), testid: "stat-total-assessments" },
              { icon: Crown, label: "Active Members", val: (stats.active_members ?? 0).toLocaleString(), testid: "stat-active-members" },
              { icon: AlertOctagon, label: "High Risk", val: (stats.zones?.red ?? 0).toLocaleString(), testid: "stat-high-risk" },
              { icon: Crown, label: "Founding Claimed", val: `${stats.founding?.claimed ?? 0} / ${stats.founding?.limit ?? 100}`, testid: "stat-founding" },
            ].map((s) => (
              <div key={s.label} className="bg-[#0c0c0c] p-6" data-testid={s.testid}>
                <s.icon size={18} className="text-amber-500 mb-3" strokeWidth={1.5} />
                <div className="font-display text-3xl font-medium text-white">{s.val}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Global Traffic Dashboard — cities / countries / device / timeseries */}
        <GlobalTrafficDashboard />

        {/* RoyAI VIP Codes — 60-minute invite windows */}
        <RoyaiCodesAdmin />

        <div className="flex items-center gap-2 mb-6 border-b border-white/5">
          {[
            { id: "users", label: "Users" },
            { id: "assessments", label: "Assessments" },
            { id: "evidence", label: "Evidence Queue" },
          ].map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm uppercase tracking-widest font-semibold transition-colors
                ${tab === t.id ? "text-amber-500 border-b-2 border-amber-500" : "text-neutral-500 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <div className="border border-white/5 overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#121212] text-neutral-400 uppercase tracking-widest text-xs">
                  <th className="text-left px-5 py-4 font-medium">Email</th>
                  <th className="text-left px-5 py-4 font-medium">Name</th>
                  <th className="text-left px-5 py-4 font-medium">Role</th>
                  <th className="text-left px-5 py-4 font-medium">Founding</th>
                  <th className="text-left px-5 py-4 font-medium">Identity</th>
                  <th className="text-left px-5 py-4 font-medium">Score</th>
                  <th className="text-left px-5 py-4 font-medium">Membership</th>
                  <th className="text-left px-5 py-4 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-4 text-white">{u.email}</td>
                    <td className="px-5 py-4 text-neutral-300">{u.name || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs uppercase tracking-widest px-2 py-1 ${u.role === "admin" ? "bg-amber-500/15 text-amber-500" : "bg-white/5 text-neutral-400"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {u.founding_member ? (
                        <span className="text-xs uppercase tracking-widest px-2 py-1 bg-amber-500/15 text-amber-500 font-mono-tech">#{u.founding_member_number}</span>
                      ) : (
                        <span className="text-neutral-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <button data-testid={`toggle-identity-${u.user_id}`}
                        onClick={() => toggleIdentityVerified(u.user_id, u.identity_verified)}
                        className={`text-xs uppercase tracking-widest px-2 py-1 font-semibold transition-colors
                          ${u.identity_verified
                              ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
                              : "bg-white/5 text-neutral-400 hover:bg-white/10"}`}>
                        {u.identity_verified ? "Verified" : "Verify"}
                      </button>
                    </td>
                    <td className="px-5 py-4 font-mono-tech text-xs text-neutral-300">{u.latest_score ?? "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs uppercase tracking-widest px-2 py-1 ${u.membership_status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-neutral-400"}`}>
                        {u.membership_status || "free"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <button data-testid={`toggle-membership-${u.user_id}`}
                        onClick={() => toggleMembership(u.user_id, u.membership_status)}
                        className="text-xs font-semibold uppercase tracking-widest text-amber-500 hover:underline">
                        {u.membership_status === "active" ? "Revoke" : "Grant"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "assessments" && (
          <div className="border border-white/5 overflow-x-auto" data-testid="admin-assessments-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#121212] text-neutral-400 uppercase tracking-widest text-xs">
                  <th className="text-left px-5 py-4 font-medium">Email</th>
                  <th className="text-left px-5 py-4 font-medium">Score</th>
                  <th className="text-left px-5 py-4 font-medium">Zone</th>
                  <th className="text-left px-5 py-4 font-medium">Risk</th>
                  <th className="text-left px-5 py-4 font-medium">Missing</th>
                  <th className="text-left px-5 py-4 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {assessments.map((a) => (
                  <tr key={a.assessment_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-4 text-white">{a.email}</td>
                    <td className="px-5 py-4 font-mono-tech text-amber-500">{a.score}</td>
                    <td className="px-5 py-4">
                      <span className="w-3 h-3 inline-block" style={{ background: a.zone === "green" ? "#10B981" : a.zone === "yellow" ? "#F59E0B" : "#E11D48" }} />
                    </td>
                    <td className="px-5 py-4 text-neutral-300">{a.risk_rating}</td>
                    <td className="px-5 py-4 text-neutral-500">{a.missing_items?.length || 0}</td>
                    <td className="px-5 py-4 text-neutral-500 font-mono-tech text-xs">{a.created_at?.slice(0, 19).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "evidence" && (
          <div data-testid="admin-evidence-section">
            <div className="flex items-center gap-2 mb-4">
              {["pending", "approved", "rejected"].map((s) => (
                <button key={s} onClick={() => setEvidenceFilter(s)}
                  data-testid={`admin-evidence-filter-${s}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest font-mono-tech border ${evidenceFilter === s ? "border-amber-500 text-amber-500" : "border-white/10 text-neutral-400 hover:text-white"}`}>
                  {s}
                </button>
              ))}
              <div className="ml-auto text-xs text-neutral-500 font-mono-tech">{evidence.length} item(s)</div>
            </div>
            {evidence.length === 0 ? (
              <div className="border border-white/5 p-8 text-sm text-neutral-500" data-testid="admin-evidence-empty">No {evidenceFilter} submissions.</div>
            ) : (
              <div className="space-y-3">
                {evidence.map((e) => (
                  <div key={e.id} data-testid={`admin-evidence-row-${e.id}`}
                    className="border border-white/5 bg-[#0c0c0c] p-5 flex flex-wrap gap-4 items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck size={14} className="text-amber-500" />
                        <span className="text-sm font-semibold text-white capitalize">{e.type}</span>
                        <span className={`font-mono-tech text-xs uppercase tracking-widest ${e.status === "approved" ? "text-emerald-400" : e.status === "rejected" ? "text-rose-400" : "text-amber-400"}`}>
                          · {e.status}
                        </span>
                      </div>
                      <div className="text-sm text-neutral-300">{e.user_name} <span className="text-neutral-500">· {e.user_email}</span></div>
                      {e.description && <div className="text-xs text-neutral-400 mt-1 italic">{e.description}</div>}
                      <div className="text-xs font-mono-tech text-neutral-600 mt-1">
                        {new Date(e.created_at).toLocaleString()} · {e.file_name}
                      </div>
                      {e.reviewer_notes && <div className="text-[13px] text-neutral-400 mt-1">Notes: {e.reviewer_notes}</div>}
                    </div>
                    <a href={e.file_url} target="_blank" rel="noopener noreferrer"
                      data-testid={`admin-evidence-view-${e.id}`}
                      className="px-3 py-2 border border-white/15 text-xs text-neutral-300 hover:border-amber-500 hover:text-amber-500 inline-flex items-center gap-1.5">
                      <ExternalLink size={12} /> View file
                    </a>
                    {e.status === "pending" && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => reviewEvidence(e.id, "approved", "Verified by admin")}
                          data-testid={`admin-evidence-approve-${e.id}`}
                          className="px-3 py-2 bg-emerald-500 text-black hover:brightness-110 text-xs font-semibold inline-flex items-center gap-1.5">
                          <CheckCircle2 size={12} /> Approve
                        </button>
                        <button onClick={() => {
                            const notes = prompt("Reason for rejection (visible to user):");
                            if (notes !== null) reviewEvidence(e.id, "rejected", notes);
                          }}
                          data-testid={`admin-evidence-reject-${e.id}`}
                          className="px-3 py-2 border border-rose-500/50 text-rose-400 hover:bg-rose-500/10 text-xs font-semibold inline-flex items-center gap-1.5">
                          <XCircle size={12} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
